import React, { useCallback } from 'react';
import Div from './Div';
import Paragraph from './Paragraph';
import Divider from './Divider';
import Button from './Button';
import Image from './Image';
import { useAppConfigContext } from '@/context/AppContext';
import Map from '@/components/Map';
import Link from 'next/link';
import Accordion from './crisp/AccordionNew';
import useScreen from '@/hooks/useScreen';
import Container from './Container';

function Footer() {
  const {
    state: { footer },
  } = useAppConfigContext();

  const { isDesktop, isTablet } = useScreen();

  const {
    address,
    addressLabel,
    copyright,
    country,
    footerLinks,
    moreShoppingLabel,
    moreShoppingLinks,
    socialLinks,
    columns,
    manufacturerLabel,
    manufacturer,
    cin,
    cinLabel,
    disclaimer,
  } = footer || {};

  const renderAccordianTitle = useCallback((item: any) => {
    return (
      <Button
        theme="none"
        className="!justify-start text-h-4 md:text-hd-4 text-cti-primary"
        href={item?.headingUrl || ''}
      >
        {item?.heading}
      </Button>
    );
  }, []);

  const renderAccordianContent = useCallback((item: any) => {
    return (
      <Map
        className="flex flex-col gap-s5"
        list={item?.links}
        render={({ id, label, href, target }) => (
          <Button
            key={id}
            theme="none"
            className="text-body-base !justify-start text-cti-primary"
            href={href}
            target={target}
          >
            {label}
          </Button>
        )}
      />
    );
  }, []);

  const renderFooterColumns = useCallback(
    ({ list }: any, index: number) => {
      return (
        <Accordion
          key={index}
          list={list}
          renderTitle={renderAccordianTitle}
          renderContent={renderAccordianContent}
          desktopScreen={isDesktop || isTablet}
        />
      );
    },
    [renderAccordianContent, renderAccordianTitle, isDesktop, isTablet],
  );

  const renderMoreShoppingLink = useCallback(
    ({ id, label, href }: { id: number; label: string; href: string }) => (
      <Button key={id} theme="link" className=" flex py-s4 text-button-small w-fit" href={href}>
        {label}
      </Button>
    ),
    [],
  );

  const renderSocialLink = useCallback(
    ({ id, icon, label, url }: { id: number; icon: any; label: string; url: string }) => {
      return (
        <Link key={id} href={url || ''}>
          <Image
            loading="lazy"
            key={id}
            src={icon}
            height={24}
            width={24}
            alt={label}
            data-testid={`social-platform-${label}`}
          />
        </Link>
      );
    },
    [],
  );

  const renderFooterLink = useCallback(
    ({ id, label, href }: { id: number; label: string; href: string }) => (
      <Button
        key={id}
        className="text-cti-tertiary pr-s3 border-r last:border-r-0"
        theme="none"
        href={href}
      >
        {label}
      </Button>
    ),
    [],
  );

  return (
    <footer className="bg-neutral-10 py-s6 md:py-s8 font-primary">
      <Container>
        <Map list={columns} className="grid md:grid-flow-col" render={renderFooterColumns} />

        <Div className="md:flex md:gap-s5 md:justify-start ">
          <Div className="pt-s5 md:pt-s8 pb-s5 md:pb-s6 flex flex-col gap-s2 max-w-xs md:flex-1">
            <Paragraph className="text-h-4 text-cti-primary">{addressLabel}</Paragraph>
            <Paragraph className="text-cti-tertiary">{address}</Paragraph>
          </Div>

          <Divider className="bg-neutral-50 md:hidden" />

          <Div className="pt-s5 md:pt-s8 pb-s5 md:pb-s6 flex flex-col gap-s2 max-w-xs md:flex-1">
            <Paragraph className="text-h-4 text-cti-primary">{manufacturerLabel}</Paragraph>
            <Paragraph className="text-cti-tertiary">{manufacturer}</Paragraph>
          </Div>

          <Divider className="bg-neutral-50 md:hidden" />

          <Div className="pt-s5 md:pt-s8 pb-s5 md:pb-s6 flex flex-col gap-s2 max-w-xs md:flex-1">
            <Paragraph className="text-h-4 text-cti-primary">{cinLabel}</Paragraph>
            <Paragraph className="text-cti-tertiary">{cin}</Paragraph>
          </Div>
        </Div>

        <Divider className="bg-neutral-50" />
        <Div className="pt-s6 pb-s3 flex flex-col gap-s2">
          <Paragraph className="text-h-4 text-cti-primary">{moreShoppingLabel}</Paragraph>
          <Map
            className="flex flex-col md:flex-row md:gap-s5"
            list={moreShoppingLinks}
            render={renderMoreShoppingLink}
          />
        </Div>
        <Divider className="bg-neutral-50 md:hidden" />
        <Map className="flex py-s6 gap-s6 " list={socialLinks} render={renderSocialLink} />

        <Div className="flex flex-col gap-s2 md:flex-row md:gap-s11 md:justify-between">
          <Paragraph className="text-h-4 text-cti-tertiary md:order-3 md:py-s4">
            {country}
          </Paragraph>

          <Div className="flex flex-col gap-s2 md:gap-s11 md:flex-row">
            <Paragraph className="text-cti-tertiary md:order-1 md:py-s4">{copyright}</Paragraph>

            <Paragraph className="block md:hidden text-cti-tertiary">{disclaimer}</Paragraph>

            <Map
              className="py-s4 flex gap-s3 md:order-2 "
              list={footerLinks}
              render={renderFooterLink}
            />
          </Div>
        </Div>

        <Paragraph className="hidden md:block text-cti-tertiary">{disclaimer}</Paragraph>
      </Container>
    </footer>
  );
}

export default Footer;
